import React, { useCallback, useMemo, useContext } from 'react';

import { Card, ScrollableContainer } from '@ergeon/core-components';
import { TagIcon, ArrowsUpDownIcon, QueueListIcon } from '@ergeon/icons/hero/micro';

import { Order, OrderDealStatus } from '../../types';
import { StaffFilter, useStaffFilterParams } from '../StaffFilter';
import { StaffFilterContext } from '../../context-providers/StaffFilterContext';
import LoadingState from '../LoadingState';
import { NoOrdersEmptyState } from '../EmptyStates';

import FilterDropdown from './components/FilterDropdown';
import PipelineColumn from './components/PipelineColumn';
import { ACTIVE_STATUSES, PIPELINE_COLUMNS, getOrdersByStatus, getTotalValueByStatus } from './utils/ordersPipelineHelpers';
import { useOrdersFilters } from './hooks/useOrdersFilters';
import { useOrders } from './hooks/useOrders';
import { usePipelineInfiniteScroll } from './hooks/usePipelineInfiniteScroll';

export const OrdersPageContent: React.FC = () => {
  const { selection, isSelectionInitialized } = useContext(StaffFilterContext);

  // Get API params from unified staff filter selection
  const { my_orders, staff_id } = useStaffFilterParams(selection);

  const {
    productFilter,
    statusFilter,
    sortBy,
    productLabel,
    statusLabel,
    sortLabel,
    productItems,
    statusItems,
    sortItems,
  } = useOrdersFilters();

  // Determine which statuses to fetch based on status filter
  const statuses = useMemo(() => {
    return statusFilter === 'on_hold' ? ['on_hold' as OrderDealStatus] : ACTIVE_STATUSES;
  }, [statusFilter]);

  // Build base params for API calls
  const baseParams = useMemo(() => {
    const params: {
      product?: string;
      ordering: string;
      staff_id?: number;
      my_orders?: boolean;
    } = {
      ordering: sortBy === 'created_at' ? '-created_at' : '-last_activity_date',
      staff_id,
      my_orders,
    };

    if (productFilter !== 'all') {
      params.product = productFilter;
    }

    return params;
  }, [productFilter, sortBy, staff_id, my_orders]);

  const { orders, totalCount, statusCounts, hasMore, isLoading, isInitialLoading, loadMore } = useOrders({
    statuses,
    baseParams,
    enabled: isSelectionInitialized,
  });

  const { sentinelRef } = usePipelineInfiniteScroll({
    hasMore,
    isLoading,
    isInitialLoading,
    loadMore,
  });

  const visibleColumns = useMemo(() => {
    if (statusFilter === 'on_hold') {
      return PIPELINE_COLUMNS.filter((col) => col.id === 'on_hold');
    }
    return PIPELINE_COLUMNS.filter((col) => col.id !== 'on_hold');
  }, [statusFilter]);

  const handleOrderClick = useCallback((order: Order) => {
    // TODO: [APPS-6520] Navigate to order detail page
    console.info('Order clicked:', order.id);
  }, []);

  const showEmptyState = !isLoading && !isInitialLoading && orders.length === 0;

  return (
    <Card className="flex flex-col gap-2 p-4 w-full h-full min-w-0 overflow-hidden" flavor="base" zIndex="1">
      <div className="flex justify-between items-center gap-4 min-h-8">
        <div className="flex items-center gap-3">
          <StaffFilter />
          <FilterDropdown icon={TagIcon} items={productItems} label={productLabel} title="Filter by product" />
          <FilterDropdown icon={QueueListIcon} items={statusItems} label={statusLabel} title="Filter by status" />
        </div>
        <div className="flex items-center gap-3">
          <p className="text-gray-600 dark:text-gray-400 paragraph-3">
            {totalCount} {totalCount === 1 ? 'Order' : 'Orders'}
          </p>
          <FilterDropdown
            alignment="right"
            icon={ArrowsUpDownIcon}
            items={sortItems}
            label={sortLabel}
            title="Sort by" />
        </div>
      </div>
      <div className="relative flex-1 min-h-0">
        <div className="absolute inset-0 overflow-x-auto">
          <ScrollableContainer direction="col" maskSize={0}>
            <div className="flex flex-col gap-2 min-h-full relative">
              {/* Pipeline columns - always visible */}
              <div className="flex gap-2 flex-1">
                {visibleColumns.map((column) => {
                  const columnOrders = getOrdersByStatus(orders, column.id);
                  const totalValue = getTotalValueByStatus(orders, column.id);
                  return (
                    <PipelineColumn
                      isLoading={isInitialLoading}
                      key={column.id}
                      onOrderClick={handleOrderClick}
                      orderCount={statusCounts[column.id] || 0}
                      orders={columnOrders}
                      title={column.label}
                      totalValue={totalValue} />
                  );
                })}
              </div>

              {/* Empty state overlay - shown on top of columns */}
              {showEmptyState && (
                <div className="absolute inset-0 flex items-center justify-center bg-white/80 dark:bg-gray-800/80 z-10">
                  <NoOrdersEmptyState />
                </div>
              )}

              {/* Sentinel for infinite scroll */}
              {hasMore && !isInitialLoading && <div className="h-4 shrink-0" ref={sentinelRef} />}

              {/* Loading indicator for infinite scroll */}
              {isLoading && !isInitialLoading && <LoadingState />}
            </div>
          </ScrollableContainer>
        </div>
      </div>
    </Card>
  );
};
